import { c as _c } from "react/compiler-runtime";
import { compact, difference, memoize, uniq } from 'lodash';
import React, { use } from 'react';
import { useMutation, UseQueryResult } from '@tanstack/react-query';
import { Overwrite } from 'utility-types';
import { queryClient } from 'api/reactQuery';
import { deleteCommuteProfile, CommuteProfile, CommuteProfileProcessed, PatchRequestParams, PostCommuteProfileBody, PostCommuteProfileQueryParams, PutRequestParams, patchCommuteProfile, postCommuteProfile, putCommuteProfile, useAccountCommuteProfiles } from 'api/modules/commuteProfiles';
import { useIntl } from 'locales/index';
import { useNotifications } from '@commutifi-fe/commutifi-specific/hooks';
import { DaysValueEnum, RouteLeg } from 'shared/interfaces/Commute';
import { CommuterRouting, SettingsPages } from 'src/routing';
import { sortByKey } from 'utils/helpers';
import { getMapDrawingSections, MapDrawingSection } from 'utils/map';
import { getDayLocale } from 'utils/moment';
import { useCurrentAccount } from './CurrentAccount';
export interface CommuteProfilesProviderProps {
  /**
   * Enable data fetching in the provider. This allows us to use
   * the provider at top level tree without being concerned to fetch
   * too many times the data when we don't need it
   */
  enabled?: boolean;
  children: React.ReactNode;
}
interface IModesByDay {
  [DaysValueEnum.Monday]: string[];
  [DaysValueEnum.Tuesday]: string[];
  [DaysValueEnum.Wednesday]: string[];
  [DaysValueEnum.Thursday]: string[];
  [DaysValueEnum.Friday]: string[];
  [DaysValueEnum.Saturday]: string[];
  [DaysValueEnum.Sunday]: string[];
}
export type PatchCPFunction = (data: PatchRequestParams) => void;
type DeleteFunction = (commuteProfileId: string) => Promise<void>;
export interface PostCommuteProfileOptions {
  queryParams?: PostCommuteProfileQueryParams;
  skipNotifications?: boolean;
}
type PostFunction = (data: PostCommuteProfileBody, opts?: PostCommuteProfileOptions) => Promise<CommuteProfile | undefined>;
type PutFunction = (data: PutRequestParams) => Promise<CommuteProfile | undefined>;
export interface ICommuteWithDrawing extends CommuteProfileProcessed {
  drawing: MapDrawingSection[];
}
type CommuteProfilesContextValue = Overwrite<Partial<UseQueryResult<CommuteProfileProcessed[]>>, {
  isError: boolean;
}> & {
  goingModesByCommuteDays: IModesByDay;
  returnModesByCommuteDays: IModesByDay;
  activeCommuteProfiles: ICommuteWithDrawing[];
  inactiveCommuteProfiles: ICommuteWithDrawing[];
  processingDay: DaysValueEnum | null;
  isProcessing: boolean;
  isProcessingError: boolean;
  patch: PatchCPFunction;
  create: PostFunction;
  deleteCommute: DeleteFunction;
  put: PutFunction;
  getCommuteDrawingSections: (commuteProfile: CommuteProfileProcessed | undefined) => MapDrawingSection[];
  getCommuteProfile: (cpId: string) => CommuteProfileProcessed | undefined;
};
export const CommuteProfilesContext = React.createContext<CommuteProfilesContextValue | null>(null);
const initialModesByDay: IModesByDay = {
  [DaysValueEnum.Monday]: [],
  [DaysValueEnum.Tuesday]: [],
  [DaysValueEnum.Wednesday]: [],
  [DaysValueEnum.Thursday]: [],
  [DaysValueEnum.Friday]: [],
  [DaysValueEnum.Saturday]: [],
  [DaysValueEnum.Sunday]: []
};
const extractCommuteActivities = (commute: CommuteProfileProcessed): CommuteProfileProcessed => ({
  ...commute,
  activities: uniq(compact(sortByKey(commute.goingLegs, 'order').map((leg: RouteLeg) => leg.activity)))
});

// In Use CP have a day in the days array (used at least one day of the week)
const makeGetInUseCommuteProfiles = (inUse = true) => memoize((commuteProfiles: CommuteProfileProcessed[] = []) => commuteProfiles.filter(cp => inUse ? Boolean(cp.days?.length) : !cp.days?.length).map(cp => ({
  ...cp,
  drawing: getCommuteDrawingSections(cp)
})));
const getGoingCommuteProfiles = memoize((commuteProfiles: CommuteProfileProcessed[] = []) => commuteProfiles.filter(cp => !cp.isReturn && Boolean(cp.days?.length)));
const getReturnCommuteProfiles = memoize((commuteProfiles: CommuteProfileProcessed[] = []) => commuteProfiles.filter(cp => cp.isReturn && Boolean(cp.days?.length)));
const getCommuteDrawingSections = memoize((commuteProfile: CommuteProfileProcessed | undefined) => {
  if (!commuteProfile) {
    return [];
  }
  const legs = commuteProfile.goingLegs || commuteProfile.returnLegs || [];
  return getMapDrawingSections(sortByKey(legs, 'order'));
});

/**
 * Get one commute profile. We do a find because there should be only few commute profiles
 */
const getCommuteProfile = memoize((commuteProfiles: CommuteProfileProcessed[] = []) => (commuteProfileId: string) => commuteProfiles.find(cp => cp.id === commuteProfileId));
const getModesByCommuteDay = memoize((commuteProfiles: CommuteProfileProcessed[] = [], type: 'going' | 'return' = 'going') => {
  if (!commuteProfiles.length) {
    return initialModesByDay;
  }
  const filterCommuteProfilesFn = {
    going: getGoingCommuteProfiles,
    return: getReturnCommuteProfiles
  };
  const goingCommuteProfiles = filterCommuteProfilesFn[type](commuteProfiles);
  return goingCommuteProfiles.reduce<IModesByDay>((modesByDay: IModesByDay, commuteProfile: CommuteProfileProcessed) => {
    const updatedModesByDay: IModesByDay = {
      ...modesByDay
    };
    const days = commuteProfile.days || [];
    days.forEach(day => updatedModesByDay[day] = [...updatedModesByDay[day], ...commuteProfile.orderedModes, ...(commuteProfile.activities || [])]);
    return updatedModesByDay;
  }, initialModesByDay);
});
const alterCommuteDays = (originalCommute: CommuteProfileProcessed, newCommuteDays?: DaysValueEnum[] | null) => {
  if (originalCommute.days && originalCommute.days.length > 0 && newCommuteDays) {
    const daysDiff = difference(originalCommute.days, newCommuteDays);
    return daysDiff.length >= 0 ? {
      ...originalCommute,
      days: daysDiff,
      weeklyFrequency: daysDiff.length
    } : originalCommute;
  }
  return originalCommute;
};
export const CreateCommuteScreenPathRegexp = new RegExp(`^(${CommuterRouting.settings.commute.create.path})$`);
export const CommuteDetailsScreenPathRegexp = new RegExp(`^(${CommuterRouting.settings.path}/${SettingsPages.Commute}/)[a-zA-Z0-9-]+$`);
function CommuteProfilesProvider(t0) {
  const $ = _c(75);
  const {
    enabled,
    children
  } = t0;
  const intl = useIntl();
  const commutifiNotification = useNotifications();
  const {
    id: accountId
  } = useCurrentAccount();
  let t1;
  if ($[0] !== accountId) {
    t1 = ["commute-profiles", accountId];
    $[0] = accountId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryId = t1;
  let t2;
  if ($[2] !== enabled) {
    t2 = {
      enabled
    };
    $[2] = enabled;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    isLoading,
    isFetching,
    isError,
    data,
    refetch
  } = useAccountCommuteProfiles(accountId, t2);
  let t3;
  if ($[4] !== data) {
    t3 = data?.map(extractCommuteActivities);
    $[4] = data;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const commuteProfiles = t3;
  const [updatingDay, setUpdatingDay] = React.useState(null);
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = d => {
      if (d.day) {
        setUpdatingDay(d.day);
      }
      return patchCommuteProfile(d);
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== commuteProfiles || $[8] !== commutifiNotification || $[9] !== intl || $[10] !== queryId) {
    t5 = (_, variables) => {
      queryClient.setQueryData(queryId, commuteProfiles?.map(cp => {
        if (cp.id === variables.id) {
          return {
            ...cp,
            ...variables.data,
            weeklyFrequency: variables.data.days?.length || cp.weeklyFrequency
          };
        }
        if (cp.id === variables.previousId) {
          return alterCommuteDays(cp, variables.data.days);
        }
        return cp;
      }));
      commutifiNotification.success(intl.formatMessage({
        id: "settings.commute.routes.edit.schduleDay.success"
      }, {
        day: getDayLocale(variables.day)
      }));
    };
    $[7] = commuteProfiles;
    $[8] = commutifiNotification;
    $[9] = intl;
    $[10] = queryId;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== commutifiNotification || $[13] !== intl) {
    t6 = (__0, variables_0) => {
      commutifiNotification.error({
        description: intl.formatMessage({
          id: "settings.commute.routes.edit.schduleDay.error"
        }, {
          day: getDayLocale(variables_0.day)
        })
      });
    };
    $[12] = commutifiNotification;
    $[13] = intl;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t5 || $[16] !== t6) {
    t7 = {
      mutationFn: t4,
      onSuccess: t5,
      onError: t6
    };
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const {
    isPending: isUpdating,
    isError: isUpdatingError,
    mutate: patch
  } = useMutation(t7);
  let t8;
  if ($[18] !== commuteProfiles || $[19] !== commutifiNotification || $[20] !== intl || $[21] !== queryId || $[22] !== refetch) {
    t8 = async () => {
      if (commuteProfiles && commuteProfiles.length > 0) {
        await queryClient.invalidateQueries({
          queryKey: queryId
        });
        await refetch();
      }
      commutifiNotification.success(intl.formatMessage({
        id: "commuteProfiles.update.success.message"
      }));
    };
    $[18] = commuteProfiles;
    $[19] = commutifiNotification;
    $[20] = intl;
    $[21] = queryId;
    $[22] = refetch;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== commutifiNotification || $[25] !== intl) {
    t9 = () => {
      commutifiNotification.error({
        description: intl.formatMessage({
          id: "commuteProfiles.update.error.message"
        })
      });
    };
    $[24] = commutifiNotification;
    $[25] = intl;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== t8 || $[28] !== t9) {
    t10 = {
      mutationFn: putCommuteProfile,
      onSuccess: t8,
      onError: t9
    };
    $[27] = t8;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const {
    isPending: isLoadingPut,
    isError: isPutError,
    mutateAsync: put
  } = useMutation(t10);
  let t11;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      mutationFn: postCommuteProfile
    };
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  const {
    isPending: isCreating,
    isError: isCreatingError,
    mutateAsync: triggerPost
  } = useMutation(t11);
  let t12;
  if ($[31] !== commutifiNotification || $[32] !== intl || $[33] !== queryId || $[34] !== refetch || $[35] !== triggerPost) {
    t12 = async (body, opts) => {
      if (body.days?.length === 1) {
        setUpdatingDay(body.days[0]);
      }
      const res = await triggerPost({
        body,
        queryParams: opts?.queryParams
      }, {
        onError: () => {
          if (!opts?.skipNotifications) {
            commutifiNotification.error({
              description: intl.formatMessage({
                id: "commuteProfiles.create.error.message"
              })
            });
          }
        },
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: queryId
          });
          await refetch();
          if (!opts?.skipNotifications) {
            commutifiNotification.success(intl.formatMessage({
              id: "commuteProfiles.create.success.message"
            }));
          }
        }
      });
      return res;
    };
    $[31] = commutifiNotification;
    $[32] = intl;
    $[33] = queryId;
    $[34] = refetch;
    $[35] = triggerPost;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  const create = t12;
  let t13;
  if ($[37] !== commutifiNotification || $[38] !== intl) {
    t13 = () => {
      commutifiNotification.error({
        description: intl.formatMessage({
          id: "commuteProfiles.delete.error.message"
        })
      });
    };
    $[37] = commutifiNotification;
    $[38] = intl;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== commuteProfiles || $[41] !== queryId) {
    t14 = (__1, commuteProfileId) => {
      if (commuteProfiles && commuteProfiles.length > 0) {
        queryClient.setQueryData(queryId, commuteProfiles.filter(cp_0 => cp_0.id !== commuteProfileId));
      }
    };
    $[40] = commuteProfiles;
    $[41] = queryId;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] !== t13 || $[44] !== t14) {
    t15 = {
      mutationFn: deleteCommuteProfile,
      onError: t13,
      onSuccess: t14
    };
    $[43] = t13;
    $[44] = t14;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  const {
    isPending: isDeleting,
    isError: isDeleteError,
    mutateAsync: deleteCommute
  } = useMutation(t15);
  let t16;
  if ($[46] !== commuteProfiles) {
    t16 = getModesByCommuteDay(commuteProfiles);
    $[46] = commuteProfiles;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  let t17;
  if ($[48] !== commuteProfiles) {
    t17 = getModesByCommuteDay(commuteProfiles, "return");
    $[48] = commuteProfiles;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  let t18;
  if ($[50] !== commuteProfiles) {
    t18 = makeGetInUseCommuteProfiles(true)(commuteProfiles);
    $[50] = commuteProfiles;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  let t19;
  if ($[52] !== commuteProfiles) {
    t19 = makeGetInUseCommuteProfiles(false)(commuteProfiles);
    $[52] = commuteProfiles;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  const t20 = isUpdating || isCreating ? updatingDay : null;
  const t21 = isUpdating || isCreating || isDeleting || isLoadingPut;
  const t22 = isUpdatingError || isCreatingError || isDeleteError || isPutError;
  let t23;
  if ($[54] !== commuteProfiles) {
    t23 = cpId => getCommuteProfile(commuteProfiles)(cpId);
    $[54] = commuteProfiles;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== create || $[57] !== deleteCommute || $[58] !== isError || $[59] !== isFetching || $[60] !== isLoading || $[61] !== patch || $[62] !== put || $[63] !== t16 || $[64] !== t17 || $[65] !== t18 || $[66] !== t19 || $[67] !== t20 || $[68] !== t21 || $[69] !== t22 || $[70] !== t23) {
    t24 = {
      isLoading,
      isFetching,
      isError,
      goingModesByCommuteDays: t16,
      returnModesByCommuteDays: t17,
      activeCommuteProfiles: t18,
      inactiveCommuteProfiles: t19,
      getCommuteDrawingSections,
      patch,
      processingDay: t20,
      isProcessing: t21,
      isProcessingError: t22,
      create,
      deleteCommute,
      put,
      getCommuteProfile: t23
    };
    $[56] = create;
    $[57] = deleteCommute;
    $[58] = isError;
    $[59] = isFetching;
    $[60] = isLoading;
    $[61] = patch;
    $[62] = put;
    $[63] = t16;
    $[64] = t17;
    $[65] = t18;
    $[66] = t19;
    $[67] = t20;
    $[68] = t21;
    $[69] = t22;
    $[70] = t23;
    $[71] = t24;
  } else {
    t24 = $[71];
  }
  const value = t24;
  if (!enabled) {
    return children;
  }
  let t25;
  if ($[72] !== children || $[73] !== value) {
    t25 = <CommuteProfilesContext value={value}>{children}</CommuteProfilesContext>;
    $[72] = children;
    $[73] = value;
    $[74] = t25;
  } else {
    t25 = $[74];
  }
  return t25;
}
const useCommuteProfiles = () => {
  const context = use(CommuteProfilesContext);
  if (!context) {
    throw new Error('useCommuteProfile must be used within a CommuteProfilesProvider');
  }
  return context;
};
export { CommuteProfilesProvider, useCommuteProfiles };